import React, { useEffect, useState } from 'react';

import Polling from 'handshq-app/utilities/polling';
import AttachmentIcon from 'icons/attachment.svg';
import ChevronDownIcon from 'icons/chevron-down.svg';
import ChevronUpIcon from 'icons/chevron-up.svg';
import { useTranslation } from 'react-i18next';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import DocumentGenerator from 'components/application/DocumentGenerator';
import OptionChoice from 'components/application/OptionChoice';
import Options from 'components/application/Options';

export default function TrainingDownloadOptions({
  disabled,
  documentGeneratorParams,
  documentGeneratorUrl,
  emailReportUrl,
  evidenceStatusUrl,
  setTrainingEvidenceProcessed,
  trainingEvidenceProcessed,
}) {
  const [mutableDocumentGeneratorParams, setMutableDocumentGeneratorParams] =
    useState(documentGeneratorParams);

  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.TrainingDownloadOptions',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const statusPoller = new Polling({
    interval: 1000,
    url: evidenceStatusUrl,
    responseCheck(response) {
      return response.data.processed;
    },
    successCallback: (_response) => {
      setTrainingEvidenceProcessed(true);
    },
    failureCallback: (_response) => {
      console.error('could not load attachments status');
    },
  });

  useEffect(() => {
    if (!trainingEvidenceProcessed) statusPoller.start();

    return () => {
      if (statusPoller) statusPoller.end();
    };
  }, [trainingEvidenceProcessed]);

  const startReportDownload = (extraDocumentGeneratorParams) => {
    setMutableDocumentGeneratorParams({
      ...mutableDocumentGeneratorParams,
      ...extraDocumentGeneratorParams,
    });
    setIsGeneratingDocument(true);
  };

  return (
    <>
      <Options
        btnToggle={(isOpen) => (
          <OutlinedButton
            color='grey'
            disabled={disabled}
            onClick={() => {}}
            size='sm'
          >
            <AttachmentIcon className='-tw-ml-1' height={18} width={18} />
            <span className='tw-px-1'>
              {i18nTCommon('training.trainingReport')}
            </span>
            <ChevronUpIcon
              className='-tw-mr-2'
              height={24}
              style={{ display: isOpen ? 'inline-block' : 'none' }}
              width={24}
            />
            <ChevronDownIcon
              className='-tw-mr-2'
              height={24}
              style={{ display: isOpen ? 'none' : 'inline-block' }}
              width={24}
            />
          </OutlinedButton>
        )}
        buttonDisabled={disabled}
        key='trainingDownload'
      >
        <OptionChoice
          onClick={(setOptionsOpen) => {
            startReportDownload({ with_evidence: true });
            setOptionsOpen(false);
          }}
        >
          <a className='download_button tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
            {i18nT('options.choices.downloadWithEvidence')}
          </a>
        </OptionChoice>
        <OptionChoice
          onClick={(setOptionsOpen) => {
            startReportDownload({ with_evidence: false });
            setOptionsOpen(false);
          }}
        >
          <a className='download_button tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
            {i18nT('options.choices.downloadWithoutEvidence')}
          </a>
        </OptionChoice>
        {emailReportUrl && (
          <OptionChoice>
            <a
              className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'
              href={emailReportUrl}
            >
              {i18nT('options.choices.emailReport')}
            </a>
          </OptionChoice>
        )}
      </Options>
      <DocumentGenerator
        generateParams={mutableDocumentGeneratorParams}
        generateUrl={documentGeneratorUrl}
        isGenerating={isGeneratingDocument}
        modalFailureMessage={i18nTCommon('attachments.problemCreatingPdf')}
        modalHeader={i18nTCommon('training.downloadTrainingReport')}
        modalSuccessMessage={i18nT('documentGenerator.modalSuccessMessage')}
        setIsGenerating={setIsGeneratingDocument}
      />
    </>
  );
}
